'use client';

import { chakra, RecipeVariantProps, HTMLChakraProps } from '@chakra-ui/react';
import { cardButtonRecipe } from './cardButton.recipe';
import { ReactNode } from 'react';

type CardButtonVariantProps = RecipeVariantProps<typeof cardButtonRecipe>;

export interface CardButtonProps extends HTMLChakraProps<
  'button',
  CardButtonVariantProps
> {
  /** Optional secondary line rendered under the title (children). */
  description?: ReactNode;
  'data-e2e'?: string;
}

const BaseCardButton = chakra('button', cardButtonRecipe);

/**
 * @useFor a card-style selector rendered as a real `<button>` — a
 *   full-width clickable tile with a title (children) and an optional
 *   `description` line, with themed hover / selected / disabled /
 *   focus-visible states via `cardButtonRecipe`.
 *
 *   Use this in place of the `Box as="button"` card pattern: because
 *   it's a true button you get native `disabled`, `type`, keyboard
 *   activation, and focus handling for free (no `aria-disabled` +
 *   `tabIndex` + onClick-guard workarounds). Defaults `type="button"`
 *   so a card inside a form never accidentally submits it.
 *
 *   Pass `selected` to drive a persistent highlighted state; when
 *   `selected` is controlled the component also emits `aria-pressed`
 *   for correct toggle-button semantics. Leave it unset for
 *   navigation-style cards (e.g. a picker that advances to the next
 *   step on click).
 *
 *   Use `variant="row"` for a flush list row — no border / radius /
 *   tile fill, padded for a tight list — when adopting CardButton for
 *   selectable list pickers where the bordered-card chrome would be
 *   wrong; selection then shows as a row highlight instead of a border.
 *
 *   Use `size="lg"` to scale the title / description up (and bake in a
 *   44px min touch target) for touch surfaces such as the kiosk; the
 *   default `size="md"` keeps the compact sm/xs type.
 *
 *   Examples in-tree: the Add Source template-picker cards
 *   (`sources-ui` `renderTemplateCard`).
 *
 *   For the canonical action button (primary / secondary / danger)
 *   use `<Button>`. For a non-interactive summary tile use
 *   `<CardComponent>`; for a settings/section container use
 *   `<SectionCard>`.
 */
export const CardButton = ({
  children,
  description,
  selected,
  variant = 'card',
  size = 'md',
  type = 'button',
  ...props
}: CardButtonProps) => {
  return (
    <BaseCardButton
      type={type}
      variant={variant}
      size={size}
      selected={selected}
      aria-pressed={selected === undefined ? undefined : Boolean(selected)}
      {...props}
    >
      {/* Row titles sit a touch lighter (500) to match the flush list
          pickers; card titles stay 600 for the bordered tile. */}
      <chakra.span
        fontSize={size === 'lg' ? 'md' : 'sm'}
        fontWeight={variant === 'row' ? '500' : '600'}
        color="text.primary"
      >
        {children as ReactNode}
      </chakra.span>
      {description && (
        <chakra.span
          fontSize={size === 'lg' ? 'sm' : 'xs'}
          color="text.secondary"
        >
          {description}
        </chakra.span>
      )}
    </BaseCardButton>
  );
};
